import {useCallback, useMemo, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {useDebounce} from '@common-web/hooks/use-debounce';
import type {SortDirection, UseDataTableOptions, UseDataTableReturn} from './types';
import {TABLE_PAGE_SIZE} from '@common/constants/pagination';
import {
  buildUrlKeys,
  coerceFilterValue,
  decodeSort,
  encodeSort,
  filterKey,
  nextSortDirection,
} from './url-state';

const DEFAULT_PAGE_SIZE = TABLE_PAGE_SIZE;
const DEFAULT_DEBOUNCE_MS = 500;

interface InternalState<TFilters> {
  page: number;
  pageSize: number;
  search: string;
  sort: {key: string; direction: SortDirection} | null;
  filters: TFilters;
}

export function useDataTable<TFilters extends Record<string, unknown> = {}>(
  options: UseDataTableOptions<TFilters> = {},
): UseDataTableReturn<TFilters> {
  const {
    defaultPageSize = DEFAULT_PAGE_SIZE,
    defaultFilters,
    defaultSort = null,
    searchDebounce = DEFAULT_DEBOUNCE_MS,
    syncToUrl = true,
  } = options;

  const useUrl = syncToUrl !== false;
  const prefix = typeof syncToUrl === 'string' ? syncToUrl : undefined;
  const keys = useMemo(() => buildUrlKeys(prefix), [prefix]);
  const initialFilters = (defaultFilters ?? ({} as TFilters)) as TFilters;

  // URL-mode state: sourced from useSearchParams
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Local-mode fallback state
  const [localState, setLocalState] = useState<InternalState<TFilters>>({
    page: 1,
    pageSize: defaultPageSize,
    search: '',
    sort: defaultSort,
    filters: initialFilters,
  });

  // Selection always lives in local state (never URL-synced)
  const [selection, setSelection] = useState<Set<string>>(new Set());

  // ----- Reads -----
  const page = useUrl
    ? Math.max(1, parseInt(searchParams.get(keys.page) || '1', 10) || 1)
    : localState.page;

  const pageSize = useUrl
    ? Math.max(
        1,
        parseInt(searchParams.get(keys.pageSize) || String(defaultPageSize), 10) || defaultPageSize,
      )
    : localState.pageSize;

  const search = useUrl ? (searchParams.get(keys.search) ?? '') : localState.search;
  const sort = useUrl ? (decodeSort(searchParams.get(keys.sort)) ?? defaultSort) : localState.sort;

  const filterValues = useMemo<TFilters>(() => {
    if (!useUrl) return localState.filters;
    if (!defaultFilters) return {} as TFilters;
    const result: Record<string, unknown> = {};
    for (const k of Object.keys(defaultFilters) as (keyof TFilters & string)[]) {
      const raw = searchParams.get(filterKey(k, prefix));
      result[k] = raw === null ? defaultFilters[k] : coerceFilterValue(raw, defaultFilters[k]);
    }
    return result as TFilters;
  }, [useUrl, defaultFilters, searchParams, prefix, localState.filters]);

  const debouncedSearch = useDebounce(search, searchDebounce);

  // ----- Writes -----
  const writeUrl = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        // A bare replace nulls `location.state`, which pages use to carry e.g. a Back target.
        {replace: true, state: location.state},
      );
    },
    [setSearchParams, location.state],
  );

  const setPage = useCallback(
    (n: number) => {
      if (useUrl) {
        writeUrl((p) => {
          if (n === 1) p.delete(keys.page);
          else p.set(keys.page, String(n));
        });
      } else {
        setLocalState((s) => ({...s, page: n}));
      }
    },
    [useUrl, writeUrl, keys.page],
  );

  const setPageSize = useCallback(
    (n: number) => {
      if (useUrl) {
        writeUrl((p) => {
          if (n === defaultPageSize) p.delete(keys.pageSize);
          else p.set(keys.pageSize, String(n));
          p.delete(keys.page);
        });
      } else {
        setLocalState((s) => ({...s, pageSize: n, page: 1}));
      }
    },
    [useUrl, writeUrl, keys.pageSize, keys.page, defaultPageSize],
  );

  const setSearch = useCallback(
    (s: string) => {
      if (useUrl) {
        writeUrl((p) => {
          if (!s) p.delete(keys.search);
          else p.set(keys.search, s);
          p.delete(keys.page);
        });
      } else {
        setLocalState((prev) => ({...prev, search: s, page: 1}));
      }
    },
    [useUrl, writeUrl, keys.search, keys.page],
  );

  const setFilter = useCallback(
    <K extends keyof TFilters>(key: K, value: TFilters[K]) => {
      const k = key as keyof TFilters & string;
      if (useUrl) {
        writeUrl((p) => {
          const def = defaultFilters?.[key];
          const isDefault =
            value === def ||
            (def === undefined && (value === '' || value === null || value === undefined));
          if (isDefault) p.delete(filterKey(k, prefix));
          else p.set(filterKey(k, prefix), String(value));
          p.delete(keys.page);
        });
      } else {
        setLocalState((s) => ({
          ...s,
          filters: {...s.filters, [k]: value},
          page: 1,
        }));
      }
    },
    [useUrl, writeUrl, defaultFilters, prefix, keys.page],
  );

  const resetFilters = useCallback(() => {
    if (useUrl) {
      writeUrl((p) => {
        if (defaultFilters) {
          for (const k of Object.keys(defaultFilters)) {
            p.delete(filterKey(k, prefix));
          }
        }
        p.delete(keys.page);
      });
    } else {
      setLocalState((s) => ({...s, filters: initialFilters, page: 1}));
    }
  }, [useUrl, writeUrl, defaultFilters, prefix, keys.page, initialFilters]);

  const setSort = useCallback(
    (next: {key: string; direction: SortDirection} | null) => {
      if (useUrl) {
        writeUrl((p) => {
          const encoded = encodeSort(next);
          if (encoded === null) p.delete(keys.sort);
          else p.set(keys.sort, encoded);
          p.delete(keys.page);
        });
      } else {
        setLocalState((s) => ({...s, sort: next, page: 1}));
      }
    },
    [useUrl, writeUrl, keys.sort, keys.page],
  );

  const toggleSort = useCallback(
    (key: string) => setSort(nextSortDirection(sort, key)),
    [sort, setSort],
  );

  const toggleRow = useCallback((rowKey: string) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const toggleAll = useCallback((rowKeys: string[]) => {
    setSelection((prev) => {
      const allSelected = rowKeys.length > 0 && rowKeys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allSelected) {
        for (const k of rowKeys) next.delete(k);
      } else {
        for (const k of rowKeys) next.add(k);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelection(new Set()), []);

  const isAllSelected = useCallback(
    (rowKeys: string[]) => rowKeys.length > 0 && rowKeys.every((k) => selection.has(k)),
    [selection],
  );

  return {
    page,
    pageSize,
    setPage,
    setPageSize,
    search,
    debouncedSearch,
    setSearch,
    filterValues,
    setFilter,
    resetFilters,
    sort,
    toggleSort,
    setSort,
    selection,
    toggleRow,
    toggleAll,
    clearSelection,
    isAllSelected,
  };
}
